import React from 'react';

const ResumeTemplatePreview = ({ template, resumeData, customization = {}, className = "" }) => {
  // Helper functions to render sections for AI-generated templates
  const renderExperience = (experience) => {
    if (!experience || experience.length === 0) return '';
    return experience.map(exp => `
      <div class="experience-item">
        <h3>${exp.title || 'Job Title'}</h3>
        <div class="company">${exp.company || 'Company Name'}</div>
        <div class="date">${exp.startDate || 'Start'} - ${exp.endDate || 'End'}</div>
        <div class="description">${exp.description || ''}</div>
      </div>
    `).join('');
  };

  const renderEducation = (education) => {
    if (!education || education.length === 0) return '';
    return education.map(edu => `
      <div class="education-item">
        <div class="date">${edu.graduationYear || 'Year'}</div>
        <h3>${edu.institution || 'Institution'}</h3>
        <div class="institution">${edu.degree || 'Degree'}</div>
        ${edu.gpa ? `<div class="gpa">GPA: ${edu.gpa}</div>` : ''}
      </div>
    `).join('');
  };

  const renderSkills = (skills) => {
    if (!skills || skills.length === 0) return '';
    return skills.map(skill => `<span class="skill-tag">${skill}</span>`).join('');
  };

  const renderProjects = (projects) => {
    if (!projects || projects.length === 0) return '';
    return projects.map(proj => `
      <div class="project-item">
        <h3>${proj.name || 'Project Name'}</h3>
        <div class="description">${proj.description || ''}</div>
      </div>
    `).join('');
  };

  const renderCertifications = (certifications) => {
    if (!certifications || certifications.length === 0) return '';
    return certifications.map(cert => `
      <div class="certification-item">
        <h3>${cert.name || 'Certificate'}</h3>
        <div class="issuer">${cert.issuer || 'Issuer'}</div>
        <div class="date">${cert.date || 'Date'}</div>
      </div>
    `).join('');
  };

  // Ensure we have valid data
  const safeResumeData = resumeData || {
    personalInfo: {
      fullName: 'John Doe',
      jobTitle: 'Software Engineer',
      email: 'john.doe@email.com',
      phone: '(555) 123-4567',
      location: 'San Francisco, CA'
    },
    summary: 'Experienced software engineer with 5+ years of experience in full-stack development...',
    experience: [
      {
        id: 1,
        title: 'Senior Software Engineer',
        company: 'Tech Company',
        startDate: '2020',
        endDate: 'Present',
        description: 'Led development of web applications using React and Node.js...'
      }
    ],
    education: [
      {
        id: 1,
        degree: 'Bachelor of Science in Computer Science',
        institution: 'University Name',
        graduationYear: '2018'
      }
    ]
  };

  const renderTemplate = () => {
    if (!template) {
      return <DefaultTemplate resumeData={safeResumeData} />;
    }

    // Always use simple React templates - skip AI-generated HTML/CSS templates
    // Use template ID to determine which React template to render
    const templateId = template.id || template.database_id;
    
    // Map template IDs to React templates
    // For AI-generated templates, default to ProfessionalTemplate1 (simple two-column design)
    switch (templateId) {
      case 'modern-1':
        return <ModernTemplate1 resumeData={safeResumeData} customization={customization} />;
      case 'modern-2':
        return <ModernTemplate2 resumeData={safeResumeData} customization={customization} />;
      case 'professional-1':
      case 'academic-1':
      case 'healthcare-1':
      case 'finance-1':
      case 'academic-2':
      case 'healthcare-2':
        return <ProfessionalTemplate1 resumeData={safeResumeData} customization={customization} />;
      case 'professional-2':
        return <ProfessionalTemplate2 resumeData={safeResumeData} customization={customization} />;
      case 'creative-1':
      case 'creative-2':
      case 'sales-1':
        return <CreativeTemplate1 resumeData={safeResumeData} customization={customization} />;
      case 'executive-1':
      case 'executive-2':
        return <ExecutiveTemplate1 resumeData={safeResumeData} customization={customization} />;
      case 'minimalist-1':
      case 'minimalist-2':
        return <MinimalistTemplate1 resumeData={safeResumeData} customization={customization} />;
      case 'two-column-1':
        return <TwoColumnTemplate1 resumeData={safeResumeData} customization={customization} />;
      case 'classic-1':
        return <ClassicTemplate1 resumeData={safeResumeData} customization={customization} />;
      case 'tech-1':
      case 'tech-2':
        return <TechTemplate1 resumeData={safeResumeData} customization={customization} />;
      default:
        // Default to ProfessionalTemplate1 for any unknown or AI-generated templates
        return <ProfessionalTemplate1 resumeData={safeResumeData} customization={customization} />;
    }
  };

  return (
    <div className={`resume-preview ${className}`}>
      {renderTemplate()}
    </div>
  );
};

// Modern Template 1 - Clean and Contemporary
const ModernTemplate1 = ({ resumeData, customization = {} }) => {
  const getFontSize = () => {
    switch (customization.fontSize) {
      case 'small': return 'text-sm';
      case 'large': return 'text-lg';
      default: return 'text-base';
    }
  };

  const getSpacing = () => {
    switch (customization.spacing) {
      case 'compact': return 'space-y-4';
      case 'relaxed': return 'space-y-8';
      default: return 'space-y-6';
    }
  };

  return (
    <div 
      className={`p-6 font-sans ${getFontSize()}`}
      style={{
        backgroundColor: customization.backgroundColor || '#FFFFFF',
        color: customization.textColor || '#111827',
        fontFamily: customization.fontFamily || 'Inter'
      }}
    >
      {/* Header with Profile Picture */}
      <div className="flex items-start gap-6 mb-6">
        {/* Profile Picture on Top Left */}
        {resumeData?.personalInfo?.profilePicture && (
          <div className="flex-shrink-0">
            <img 
              src={resumeData.personalInfo.profilePicture} 
              alt="Profile" 
              className="w-24 h-24 rounded-full object-cover border-4 shadow-lg"
              style={{ borderColor: customization.primaryColor || '#3B82F6' }}
            />
          </div>
        )}
        
        {/* Name and Contact Info */}
        <div 
          className="flex-1 border-l-4 pl-4"
          style={{ borderColor: customization.primaryColor || '#3B82F6' }}
        >
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: customization.headingColor || '#111827' }}
          >
            {resumeData?.personalInfo?.fullName || 'Your Name'}
          </h1>
          <p 
            className="text-lg font-medium"
            style={{ color: customization.primaryColor || '#3B82F6' }}
          >
            {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
          </p>
          <div className="flex flex-wrap gap-4 mt-2 text-sm" style={{ color: customization.textColor || '#6B7280' }}>
            <span>{resumeData?.personalInfo?.email || 'email@example.com'}</span>
            <span>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</span>
            <span>{resumeData?.personalInfo?.location || 'City, State'}</span>
          </div>
        </div>
      </div>
      
      <div className={getSpacing()}>
        <div>
          <h2 
            className="text-xl font-bold mb-3 border-b-2 pb-1"
            style={{ 
              color: customization.headingColor || '#111827',
              borderColor: customization.primaryColor || '#3B82F6'
            }}
          >
            Professional Summary
          </h2>
          <p 
            className="leading-relaxed"
            style={{ color: customization.textColor || '#374151' }}
          >
            {resumeData?.summary || 'Write your professional summary here...'}
          </p>
        </div>
        
        <div>
          <h2 
            className="text-xl font-bold mb-3 border-b-2 pb-1"
            style={{ 
              color: customization.headingColor || '#111827',
              borderColor: customization.primaryColor || '#3B82F6'
            }}
          >
            Experience
          </h2>
          <div className="space-y-4">
            {resumeData?.experience?.map((exp) => (
              <div key={exp.id} className="border-l-2 pl-4" style={{ borderColor: customization.secondaryColor || '#E5E7EB' }}>
                <h3 className="font-bold" style={{ color: customization.headingColor || '#111827' }}>
                  {exp.title || 'Job Title'}
                </h3>
                <p 
                  className="font-medium"
                  style={{ color: customization.primaryColor || '#3B82F6' }}
                >
                  {exp.company || 'Company Name'}
                </p>
                <p className="text-sm" style={{ color: customization.textColor || '#6B7280' }}>
                  {exp.startDate || 'Start'} - {exp.endDate || 'End'}
                </p>
                <p className="mt-2" style={{ color: customization.textColor || '#374151' }}>
                  {exp.description || 'Job description...'}
                </p>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h2 
            className="text-xl font-bold mb-3 border-b-2 pb-1"
            style={{ 
              color: customization.headingColor || '#111827',
              borderColor: customization.primaryColor || '#3B82F6'
            }}
          >
            Education
          </h2>
          <div className="space-y-2">
            {resumeData?.education?.map((edu) => (
              <div key={edu.id}>
                <h3 className="font-bold" style={{ color: customization.headingColor || '#111827' }}>
                  {edu.degree || 'Degree'}
                </h3>
                <p style={{ color: customization.primaryColor || '#3B82F6' }}>
                  {edu.institution || 'Institution'}
                </p>
                <p className="text-sm" style={{ color: customization.textColor || '#6B7280' }}>
                  {edu.graduationYear || 'Year'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Section */}
        {resumeData?.skills && resumeData.skills.length > 0 && (
          <div>
            <h2 
              className="text-xl font-bold mb-3 border-b-2 pb-1"
              style={{ 
                color: customization.headingColor || '#111827',
                borderColor: customization.primaryColor || '#3B82F6'
              }}
            >
              Skills
            </h2>
            <div className="flex flex-wrap gap-2">
              {resumeData.skills.map((skill, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 rounded-full text-sm font-medium"
                  style={{ 
                    backgroundColor: customization.accentColor || '#F59E0B',
                    color: '#FFFFFF'
                  }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Certifications Section */}
        {resumeData?.certifications && resumeData.certifications.length > 0 && (
          <div>
            <h2 
              className="text-xl font-bold mb-3 border-b-2 pb-1"
              style={{ 
                color: customization.headingColor || '#111827',
                borderColor: customization.primaryColor || '#3B82F6'
              }}
            >
              Certifications
            </h2>
            <div className="space-y-2">
              {resumeData.certifications.map((cert) => (
                <div key={cert.id}>
                  <h3 className="font-bold" style={{ color: customization.headingColor || '#111827' }}>
                    {cert.name || 'Certificate Name'}
                  </h3>
                  <p style={{ color: customization.primaryColor || '#3B82F6' }}>
                    {cert.issuer || 'Issuing Organization'}
                  </p>
                  <p className="text-sm" style={{ color: customization.textColor || '#6B7280' }}>
                    {cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Professional Template 1 - Two Column Layout (Exact Design Match)
const ProfessionalTemplate1 = ({ resumeData, customization = {} }) => (
  <div className="bg-white text-gray-900 font-sans" style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.12)' }}>
    <div className="flex min-h-screen">
      {/* Left Column - Solid Dark Blue Background (Exact Match) */}
      <div className="w-2/5 text-white p-8 relative" style={{ backgroundColor: '#1e3a8a' }}>
        
        {/* Profile Picture - Top of Sidebar */}
        {resumeData?.personalInfo?.profilePicture && (
          <div className="text-center mb-8">
            <img 
              src={resumeData.personalInfo.profilePicture} 
              alt="Profile" 
              className="w-32 h-32 rounded-full mx-auto object-cover border-2 border-white shadow-lg"
              style={{ borderWidth: '3px' }}
            />
          </div>
        )}
        
        <div className="space-y-6">
          {/* CONTACT Section */}
          <div>
            <h3 className="text-xs font-bold text-white mb-4 uppercase tracking-wider pb-2 relative" style={{ letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
              CONTACT
            </h3>
            <div className="space-y-3 text-sm" style={{ lineHeight: '1.8' }}>
              {resumeData?.personalInfo?.phone && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>📱</span>
                  <span className="text-white">{resumeData.personalInfo.phone}</span>
                </div>
              )}
              {resumeData?.personalInfo?.email && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>✉️</span>
                  <span className="text-white break-words">{resumeData.personalInfo.email}</span>
                </div>
              )}
              {resumeData?.personalInfo?.location && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>📍</span>
                  <span className="text-white">{resumeData.personalInfo.location}</span>
                </div>
              )}
              {resumeData?.personalInfo?.website && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>🌐</span>
                  <span className="text-white break-words">{resumeData.personalInfo.website}</span>
                </div>
              )}
              {resumeData?.personalInfo?.linkedin && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>💼</span>
                  <span className="text-white break-words">{resumeData.personalInfo.linkedin}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* EDUCATION Section */}
          {resumeData?.education && resumeData.education.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-white mb-4 uppercase tracking-wider pb-2 relative" style={{ letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
                EDUCATION
              </h3>
              {resumeData.education.map((edu) => (
                <div key={edu.id} className="mb-5 pb-4 border-b border-white/15 last:border-0">
                  <p className="text-xs text-white font-semibold mb-1">{edu.graduationYear || 'Year'}</p>
                  <p className="font-bold text-white mb-1 text-sm">{edu.institution || 'Institution'}</p>
                  <p className="text-sm text-white">{edu.degree || 'Degree'}</p>
                  {edu.gpa && <p className="text-xs text-white/80 mt-1">GPA: {edu.gpa}</p>}
                </div>
              ))}
            </div>
          )}

          {/* SKILLS Section - Bullet List Style */}
          {resumeData?.skills && resumeData.skills.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-white mb-4 uppercase tracking-wider pb-2 relative" style={{ letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
                SKILLS
              </h3>
              <div className="space-y-2 text-sm">
                {resumeData.skills.map((skill, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-white mt-1">•</span>
                    <span className="text-white">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications in Left Column */}
          {resumeData?.certifications && resumeData.certifications.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-300 mb-2">CERTIFICATIONS</h3>
              <div className="space-y-2">
                {resumeData.certifications.map((cert) => (
                  <div key={cert.id}>
                    <p className="text-xs font-medium">{cert.name || 'Certificate'}</p>
                    <p className="text-xs text-gray-300">{cert.issuer || 'Issuer'}</p>
                    <p className="text-xs text-gray-400">{cert.date || 'Date'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Right Column - White Background */}
      <div className="w-3/5 p-8 bg-white">
        {/* Header Section - Exact Match */}
        <div className="mb-6 pb-4 relative">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 uppercase" style={{ letterSpacing: '1px' }}>
            {resumeData?.personalInfo?.fullName || 'Your Name'}
          </h1>
          <h2 className="text-base font-normal text-gray-700 uppercase mb-3" style={{ letterSpacing: '0.5px' }}>
            {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
          </h2>
          <div className="h-0.5 bg-blue-400 w-24"></div>
        </div>
        
        <div className="space-y-8">
          {/* PROFILE Section */}
          {resumeData?.summary && (
            <div className="mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wide" style={{ letterSpacing: '1px' }}>
                PROFILE
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed" style={{ lineHeight: '1.7' }}>
                {resumeData?.summary || 'Write your professional summary here...'}
              </p>
            </div>
          )}
          
          {/* WORK EXPERIENCE Section */}
          {resumeData?.experience && resumeData.experience.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-4 uppercase tracking-wide" style={{ letterSpacing: '1px' }}>
                WORK EXPERIENCE
              </h2>
              <div className="space-y-5">
                {resumeData.experience.map((exp) => (
                  <div key={exp.id} className="mb-4">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <h3 className="font-bold text-gray-800 text-base mb-0.5">{exp.company || 'Company Name'}</h3>
                        <p className="text-gray-700 font-medium text-sm">{exp.title || 'Job Title'}</p>
                      </div>
                      <span className="text-xs text-gray-600 font-normal">
                        {exp.startDate || 'Start'} - {exp.endDate || 'End'}
                      </span>
                    </div>
                    {exp.description && (
                      <ul className="text-gray-700 text-sm leading-relaxed mt-2 pl-5 list-none">
                        {exp.description.split('\n').map((line, idx) => (
                          <li key={idx} className="mb-1 relative" style={{ paddingLeft: '15px' }}>
                            <span className="absolute left-0 text-blue-600">•</span>
                            {line}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Creative Template 1 - Colorful and Modern
const CreativeTemplate1 = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-6 text-gray-900 font-sans">
    <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-lg mb-6">
      <div className="flex items-start gap-6">
        {/* Profile Picture on Top Left */}
        {resumeData?.personalInfo?.profilePicture && (
          <div className="flex-shrink-0">
            <img 
              src={resumeData.personalInfo.profilePicture} 
              alt="Profile" 
              className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
            />
          </div>
        )}
        
        <div className="flex-1">
          <h1 className="text-4xl font-bold mb-2">
            {resumeData?.personalInfo?.fullName || 'Your Name'}
          </h1>
          <p className="text-xl text-purple-100">
            {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
          </p>
          <div className="flex flex-wrap gap-4 mt-4 text-purple-100">
            <span>{resumeData?.personalInfo?.email || 'email@example.com'}</span>
            <span>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</span>
            <span>{resumeData?.personalInfo?.location || 'City, State'}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <h2 className="text-xl font-bold text-purple-600 mb-3 flex items-center">
          <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
          About Me
        </h2>
        <p className="text-gray-700">
          {resumeData?.summary || 'Write your professional summary here...'}
        </p>
      </div>
      
      <div>
        <h2 className="text-xl font-bold text-purple-600 mb-3 flex items-center">
          <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
          Experience
        </h2>
        <div className="space-y-3">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id} className="border-l-4 border-purple-200 pl-4">
              <h3 className="font-bold text-gray-900">{exp.title || 'Job Title'}</h3>
              <p className="text-purple-600">{exp.company || 'Company Name'}</p>
              <p className="text-sm text-gray-600">{exp.startDate || 'Start'} - {exp.endDate || 'End'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-purple-600 mb-3 flex items-center">
            <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
            Skills
          </h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-purple-600 mb-3 flex items-center">
            <div className="w-2 h-2 bg-purple-600 rounded-full mr-2"></div>
            Certifications
          </h2>
          <div className="space-y-2">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id} className="border-l-4 border-purple-200 pl-4">
                <h3 className="font-bold text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-purple-600">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-sm text-gray-600">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Executive Template 1 - Sophisticated Design
const ExecutiveTemplate1 = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-8 text-gray-900 font-serif">
    <div className="flex items-start gap-6 border-b-2 border-gray-800 pb-6 mb-6">
      {/* Profile Picture on Top Left */}
      {resumeData?.personalInfo?.profilePicture && (
        <div className="flex-shrink-0">
          <img 
            src={resumeData.personalInfo.profilePicture} 
            alt="Profile" 
            className="w-24 h-24 rounded-full object-cover border-4 border-gray-800 shadow-lg"
          />
        </div>
      )}
      
      <div className="flex-1 text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {resumeData?.personalInfo?.fullName || 'Your Name'}
        </h1>
        <p className="text-xl text-gray-600">
          {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
        </p>
        <div className="flex justify-center gap-6 mt-4 text-gray-600">
          <span>{resumeData?.personalInfo?.email || 'email@example.com'}</span>
          <span>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</span>
          <span>{resumeData?.personalInfo?.location || 'City, State'}</span>
        </div>
      </div>
    </div>
    
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
          EXECUTIVE SUMMARY
        </h2>
        <p className="text-gray-700 text-center leading-relaxed">
          {resumeData?.summary || 'Write your professional summary here...'}
        </p>
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
          PROFESSIONAL EXPERIENCE
        </h2>
        <div className="space-y-6">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id} className="text-center">
              <h3 className="text-xl font-bold text-gray-900">{exp.title || 'Job Title'}</h3>
              <p className="text-lg text-gray-600">{exp.company || 'Company Name'}</p>
              <p className="text-gray-500 mb-4">{exp.startDate || 'Start'} - {exp.endDate || 'End'}</p>
              <p className="text-gray-700 max-w-2xl mx-auto">{exp.description || 'Job description...'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            CORE COMPETENCIES
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            PROFESSIONAL CERTIFICATIONS
          </h2>
          <div className="space-y-4">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id} className="text-center">
                <h3 className="text-lg font-bold text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-gray-600">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-gray-500">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Minimalist Template 1 - Clean and Simple
const MinimalistTemplate1 = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-8 text-gray-900 font-sans">
    <div className="flex items-start gap-6 mb-8">
      {/* Profile Picture on Top Left */}
      {resumeData?.personalInfo?.profilePicture && (
        <div className="flex-shrink-0">
          <img 
            src={resumeData.personalInfo.profilePicture} 
            alt="Profile" 
            className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 shadow-md"
          />
        </div>
      )}
      
      <div className="flex-1 text-center">
        <h1 className="text-3xl font-light text-gray-900 mb-2">
          {resumeData?.personalInfo?.fullName || 'Your Name'}
        </h1>
        <p className="text-lg text-gray-600 font-light">
          {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
        </p>
        <div className="flex justify-center gap-6 mt-4 text-sm text-gray-500">
          <span>{resumeData?.personalInfo?.email || 'email@example.com'}</span>
          <span>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</span>
          <span>{resumeData?.personalInfo?.location || 'City, State'}</span>
        </div>
      </div>
    </div>
    
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-300 pb-1">
          Summary
        </h2>
        <p className="text-gray-700 leading-relaxed">
          {resumeData?.summary || 'Write your professional summary here...'}
        </p>
      </div>
      
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-300 pb-1">
          Experience
        </h2>
        <div className="space-y-6">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id}>
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-medium text-gray-900">{exp.title || 'Job Title'}</h3>
                <span className="text-sm text-gray-500">{exp.startDate || 'Start'} - {exp.endDate || 'End'}</span>
              </div>
              <p className="text-gray-600 mb-2">{exp.company || 'Company Name'}</p>
              <p className="text-gray-700">{exp.description || 'Job description...'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-300 pb-1">
            Skills
          </h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-300 pb-1">
            Certifications
          </h2>
          <div className="space-y-3">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id}>
                <h3 className="font-medium text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-gray-600 text-sm">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-gray-500 text-sm">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

// Additional templates...
const ModernTemplate2 = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-6 text-gray-900 font-sans">
    <div className="flex items-start gap-6 mb-6">
      {/* Profile Picture on Top Left */}
      {resumeData?.personalInfo?.profilePicture && (
        <div className="flex-shrink-0">
          <img 
            src={resumeData.personalInfo.profilePicture} 
            alt="Profile" 
            className="w-24 h-24 rounded-full object-cover border-4 border-blue-500 shadow-lg"
          />
        </div>
      )}
      
      <div className="flex-1 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {resumeData?.personalInfo?.fullName || 'Your Name'}
          </h1>
          <p className="text-lg text-gray-600">
            {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
          </p>
        </div>
        <div className="text-right text-sm text-gray-600">
          <p>{resumeData?.personalInfo?.email || 'email@example.com'}</p>
          <p>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</p>
          <p>{resumeData?.personalInfo?.location || 'City, State'}</p>
        </div>
      </div>
    </div>
    
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">Professional Summary</h2>
        <p className="text-gray-700">{resumeData?.summary || 'Write your professional summary here...'}</p>
      </div>
      
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">Experience</h2>
        <div className="space-y-4">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id}>
              <h3 className="font-bold text-gray-900">{exp.title || 'Job Title'}</h3>
              <p className="text-gray-600">{exp.company || 'Company Name'} | {exp.startDate || 'Start'} - {exp.endDate || 'End'}</p>
              <p className="text-gray-700 mt-2">{exp.description || 'Job description...'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Skills</h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Certifications</h2>
          <div className="space-y-2">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id}>
                <h3 className="font-bold text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-gray-600">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-sm text-gray-500">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

const ProfessionalTemplate2 = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-6 text-gray-900 font-sans">
    <div className="flex items-start gap-6 mb-6">
      {/* Profile Picture on Top Left */}
      {resumeData?.personalInfo?.profilePicture && (
        <div className="flex-shrink-0">
          <img 
            src={resumeData.personalInfo.profilePicture} 
            alt="Profile" 
            className="w-24 h-24 rounded-full object-cover border-4 border-gray-800 shadow-lg"
          />
        </div>
      )}
      
      <div className="flex-1 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {resumeData?.personalInfo?.fullName || 'Your Name'}
        </h1>
        <p className="text-lg text-gray-600">
          {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
        </p>
        <div className="flex justify-center gap-6 mt-4 text-gray-600">
          <span>{resumeData?.personalInfo?.email || 'email@example.com'}</span>
          <span>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</span>
          <span>{resumeData?.personalInfo?.location || 'City, State'}</span>
        </div>
      </div>
    </div>
    
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3 border-b-2 border-gray-800 pb-1">
          PROFESSIONAL SUMMARY
        </h2>
        <p className="text-gray-700">{resumeData?.summary || 'Write your professional summary here...'}</p>
      </div>
      
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3 border-b-2 border-gray-800 pb-1">
          EXPERIENCE
        </h2>
        <div className="space-y-4">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id}>
              <h3 className="font-bold text-gray-900">{exp.title || 'Job Title'}</h3>
              <p className="text-gray-600">{exp.company || 'Company Name'}</p>
              <p className="text-sm text-gray-500">{exp.startDate || 'Start'} - {exp.endDate || 'End'}</p>
              <p className="text-gray-700 mt-2">{exp.description || 'Job description...'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3 border-b-2 border-gray-800 pb-1">
            SKILLS
          </h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3 border-b-2 border-gray-800 pb-1">
            CERTIFICATIONS
          </h2>
          <div className="space-y-2">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id}>
                <h3 className="font-bold text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-gray-600">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-sm text-gray-500">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

const TwoColumnTemplate1 = ({ resumeData, customization = {} }) => (
  <div className="bg-white text-gray-900 font-sans">
    <div className="flex min-h-screen">
      {/* Left Column - Solid Dark Blue Background (Exact Match) */}
      <div className="w-2/5 text-white p-8 relative" style={{ backgroundColor: '#1e3a8a' }}>
        {/* Profile Picture - Top of Sidebar */}
        {resumeData?.personalInfo?.profilePicture && (
          <div className="text-center mb-8">
            <img 
              src={resumeData.personalInfo.profilePicture} 
              alt="Profile" 
              className="w-32 h-32 rounded-full mx-auto object-cover border-2 border-white shadow-lg"
              style={{ borderWidth: '3px' }}
            />
          </div>
        )}
        
        <div className="space-y-6">
          {/* CONTACT Section */}
          <div>
            <h3 className="text-xs font-bold text-white mb-4 uppercase tracking-wider pb-2 relative" style={{ letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
              CONTACT
            </h3>
            <div className="space-y-3 text-sm" style={{ lineHeight: '1.8' }}>
              {resumeData?.personalInfo?.phone && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>📱</span>
                  <span className="text-white">{resumeData.personalInfo.phone}</span>
                </div>
              )}
              {resumeData?.personalInfo?.email && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>✉️</span>
                  <span className="text-white break-words">{resumeData.personalInfo.email}</span>
                </div>
              )}
              {resumeData?.personalInfo?.location && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>📍</span>
                  <span className="text-white">{resumeData.personalInfo.location}</span>
                </div>
              )}
              {resumeData?.personalInfo?.website && (
                <div className="flex items-start gap-3">
                  <span className="text-base" style={{ color: '#60a5fa' }}>🌐</span>
                  <span className="text-white break-words">{resumeData.personalInfo.website}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* EDUCATION Section */}
          {resumeData?.education && resumeData.education.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-white mb-4 uppercase tracking-wider pb-2 relative" style={{ letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
                EDUCATION
              </h3>
              {resumeData.education.map((edu) => (
                <div key={edu.id} className="mb-5 pb-4 border-b border-white/15 last:border-0">
                  <p className="text-xs text-white font-semibold mb-1">{edu.graduationYear || 'Year'}</p>
                  <p className="font-bold text-white mb-1 text-sm">{edu.institution || 'Institution'}</p>
                  <p className="text-sm text-white">{edu.degree || 'Degree'}</p>
                  {edu.gpa && <p className="text-xs text-white/80 mt-1">GPA: {edu.gpa}</p>}
                </div>
              ))}
            </div>
          )}

          {/* SKILLS Section - Bullet List Style */}
          {resumeData?.skills && resumeData.skills.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-white mb-4 uppercase tracking-wider pb-2 relative" style={{ letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
                SKILLS
              </h3>
              <div className="space-y-2 text-sm">
                {resumeData.skills.map((skill, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <span className="text-white mt-1">•</span>
                    <span className="text-white">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications in Left Column */}
          {resumeData?.certifications && resumeData.certifications.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-white mb-4 uppercase tracking-wider pb-2 relative" style={{ letterSpacing: '2px', borderBottom: '2px solid rgba(255,255,255,0.3)' }}>
                CERTIFICATIONS
              </h3>
              <div className="space-y-2 text-sm">
                {resumeData.certifications.map((cert) => (
                  <div key={cert.id} className="mb-3">
                    <p className="text-sm font-medium text-white">{cert.name || 'Certificate'}</p>
                    <p className="text-xs text-white/80">{cert.issuer || 'Issuer'}</p>
                    <p className="text-xs text-white/70">{cert.date || 'Date'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Right Column - White Background */}
      <div className="w-3/5 p-8 bg-white">
        {/* Header Section - Exact Match */}
        <div className="mb-6 pb-4 relative">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 uppercase" style={{ letterSpacing: '1px' }}>
            {resumeData?.personalInfo?.fullName || 'Your Name'}
          </h1>
          <h2 className="text-base font-normal text-gray-700 uppercase mb-3" style={{ letterSpacing: '0.5px' }}>
            {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
          </h2>
          <div className="h-0.5 bg-blue-400 w-24"></div>
        </div>
        
        <div className="space-y-8">
          {/* PROFILE Section */}
          {resumeData?.summary && (
            <div className="mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-3 uppercase tracking-wide" style={{ letterSpacing: '1px' }}>
                PROFILE
              </h2>
              <p className="text-gray-700 text-sm leading-relaxed" style={{ lineHeight: '1.7' }}>
                {resumeData?.summary || 'Write your professional summary here...'}
              </p>
            </div>
          )}
          
          {/* WORK EXPERIENCE Section */}
          {resumeData?.experience && resumeData.experience.length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-bold text-gray-800 mb-4 uppercase tracking-wide" style={{ letterSpacing: '1px' }}>
                WORK EXPERIENCE
              </h2>
              <div className="space-y-5">
                {resumeData.experience.map((exp) => (
                  <div key={exp.id} className="mb-4">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <h3 className="font-bold text-gray-800 text-base mb-0.5">{exp.company || 'Company Name'}</h3>
                        <p className="text-gray-700 font-medium text-sm">{exp.title || 'Job Title'}</p>
                      </div>
                      <span className="text-xs text-gray-600 font-normal">
                        {exp.startDate || 'Start'} - {exp.endDate || 'End'}
                      </span>
                    </div>
                    {exp.description && (
                      <ul className="text-gray-700 text-sm leading-relaxed mt-2 pl-5 list-none">
                        {exp.description.split('\n').map((line, idx) => (
                          <li key={idx} className="mb-1 relative" style={{ paddingLeft: '15px' }}>
                            <span className="absolute left-0 text-blue-600">•</span>
                            {line}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

const ClassicTemplate1 = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-8 text-gray-900 font-serif">
    <div className="flex items-start gap-6 mb-8">
      {/* Profile Picture on Top Left */}
      {resumeData?.personalInfo?.profilePicture && (
        <div className="flex-shrink-0">
          <img 
            src={resumeData.personalInfo.profilePicture} 
            alt="Profile" 
            className="w-24 h-24 rounded-full object-cover border-4 border-gray-800 shadow-lg"
          />
        </div>
      )}
      
      <div className="flex-1 text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {resumeData?.personalInfo?.fullName || 'Your Name'}
        </h1>
        <p className="text-xl text-gray-600">
          {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
        </p>
        <div className="flex justify-center gap-8 mt-4 text-gray-600">
          <span>{resumeData?.personalInfo?.email || 'email@example.com'}</span>
          <span>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</span>
          <span>{resumeData?.personalInfo?.location || 'City, State'}</span>
        </div>
      </div>
    </div>
    
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Summary</h2>
        <p className="text-gray-700 leading-relaxed">
          {resumeData?.summary || 'Write your professional summary here...'}
        </p>
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Experience</h2>
        <div className="space-y-6">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id}>
              <h3 className="text-xl font-bold text-gray-900">{exp.title || 'Job Title'}</h3>
              <p className="text-lg text-gray-600">{exp.company || 'Company Name'}</p>
              <p className="text-gray-500 mb-4">{exp.startDate || 'Start'} - {exp.endDate || 'End'}</p>
              <p className="text-gray-700">{exp.description || 'Job description...'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Skills</h2>
          <div className="flex flex-wrap gap-3">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-4 py-2 bg-gray-100 text-gray-800 rounded text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Certifications</h2>
          <div className="space-y-4">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id}>
                <h3 className="text-lg font-bold text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-gray-600">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-gray-500">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

const TechTemplate1 = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-6 text-gray-900 font-mono">
    <div className="bg-gray-900 text-green-400 p-4 rounded mb-6">
      <div className="flex items-start gap-6">
        {/* Profile Picture on Top Left */}
        {resumeData?.personalInfo?.profilePicture && (
          <div className="flex-shrink-0">
            <img 
              src={resumeData.personalInfo.profilePicture} 
              alt="Profile" 
              className="w-24 h-24 rounded-full object-cover border-4 border-green-400 shadow-lg"
            />
          </div>
        )}
        
        <div className="flex-1">
          <h1 className="text-2xl font-bold mb-2">
            {resumeData?.personalInfo?.fullName || 'Your Name'}
          </h1>
          <p className="text-green-300">
            {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
          </p>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-green-200">
            <span>{resumeData?.personalInfo?.email || 'email@example.com'}</span>
            <span>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</span>
            <span>{resumeData?.personalInfo?.location || 'City, State'}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">// Professional Summary</h2>
        <p className="text-gray-700">{resumeData?.summary || 'Write your professional summary here...'}</p>
      </div>
      
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">// Experience</h2>
        <div className="space-y-4">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id} className="border-l-2 border-green-400 pl-4">
              <h3 className="font-bold text-gray-900">{exp.title || 'Job Title'}</h3>
              <p className="text-gray-600">{exp.company || 'Company Name'}</p>
              <p className="text-sm text-gray-500">{exp.startDate || 'Start'} - {exp.endDate || 'End'}</p>
              <p className="text-gray-700 mt-2">{exp.description || 'Job description...'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">// Skills</h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">// Certifications</h2>
          <div className="space-y-2">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id} className="border-l-2 border-green-400 pl-4">
                <h3 className="font-bold text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-gray-600">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-sm text-gray-500">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

const DefaultTemplate = ({ resumeData, customization = {} }) => (
  <div className="bg-white p-6 text-gray-900 font-sans">
    {/* Header with Profile Picture */}
    <div className="flex items-start gap-6 mb-6">
      {/* Profile Picture on Top Left */}
      {resumeData?.personalInfo?.profilePicture && (
        <div className="flex-shrink-0">
          <img 
            src={resumeData.personalInfo.profilePicture} 
            alt="Profile" 
            className="w-24 h-24 rounded-full object-cover border-4 border-blue-500 shadow-lg"
          />
        </div>
      )}
      
      {/* Name and Contact Info */}
      <div className="flex-1">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {resumeData?.personalInfo?.fullName || 'Your Name'}
        </h1>
        <p className="text-lg text-gray-600 mb-4">
          {resumeData?.personalInfo?.jobTitle || 'Your Job Title'}
        </p>
        <div className="space-y-2 text-sm text-gray-600">
          <p>{resumeData?.personalInfo?.email || 'email@example.com'}</p>
          <p>{resumeData?.personalInfo?.phone || '(555) 123-4567'}</p>
          <p>{resumeData?.personalInfo?.location || 'City, State'}</p>
        </div>
      </div>
    </div>
    
    <div className="mt-8 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">Professional Summary</h2>
        <p className="text-gray-700">{resumeData?.summary || 'Write your professional summary here...'}</p>
      </div>
      
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">Experience</h2>
        <div className="space-y-4">
          {resumeData?.experience?.map((exp) => (
            <div key={exp.id}>
              <h3 className="font-bold text-gray-900">{exp.title || 'Job Title'}</h3>
              <p className="text-gray-600">{exp.company || 'Company Name'}</p>
              <p className="text-sm text-gray-500">{exp.startDate || 'Start'} - {exp.endDate || 'End'}</p>
              <p className="text-gray-700 mt-2">{exp.description || 'Job description...'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Skills Section */}
      {resumeData?.skills && resumeData.skills.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Skills</h2>
          <div className="flex flex-wrap gap-2">
            {resumeData.skills.map((skill, index) => (
              <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {resumeData?.certifications && resumeData.certifications.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-3">Certifications</h2>
          <div className="space-y-2">
            {resumeData.certifications.map((cert) => (
              <div key={cert.id}>
                <h3 className="font-bold text-gray-900">{cert.name || 'Certificate Name'}</h3>
                <p className="text-gray-600">{cert.issuer || 'Issuing Organization'}</p>
                <p className="text-sm text-gray-500">{cert.date || 'Date'} {cert.credentialId && `• ${cert.credentialId}`}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

export default ResumeTemplatePreview;

